import React, { useEffect, useMemo, useState } from "react";

// Firebase
import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";

// PDF Libraries
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Icons
import { FiEye, FiDownload } from "react-icons/fi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaUserSlash, FaUserCheck } from "react-icons/fa";

// Custom Components
import DeleteConfirmationModal from "../../Components/DeleteConfirmationModal/DeleteConfirmationModal";
import Table from "../../Components/Table/Table";
import UserDetailModal from "../../Components/UserDetailModal/UserDetailModal";
import DeactivateAccountModal from "../../Components/DeactivateAccountModal/DeactivateAccountModal";
import Loader from "../../Components/Loader/Loader";

const Home = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "deactivated"

  const fetchData = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(
        collection(db, COLLECTION_NAME.users)
      );
      let dataList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt
            ? new Date(
              data.createdAt.seconds * 1000 +
              data.createdAt.nanoseconds / 1000000
            )
            : null,
          accountStatus: data.accountStatus || "active", // Default to active if not set
        };
      });

      // Filter by role and document status
      dataList = dataList.filter(
        (user) =>
          user.role === "passenger" && user.documentStatus === "approved"
      );

      // Filter by account status
      if (statusFilter !== "all") {
        dataList = dataList.filter(
          (user) => user.accountStatus === statusFilter
        );
      }

      // Sort by created date
      dataList = dataList.sort((a, b) =>
        sortOrder === "desc"
          ? b.createdAt - a.createdAt
          : a.createdAt - b.createdAt
      );

      setData(dataList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data: ", error);
      setLoading(false);
    }
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const closeDeleteModal = () => {
    setRideToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (rideToDelete) {
      try {
        await deleteDoc(doc(db, COLLECTION_NAME.users, rideToDelete.id));
        setData((prevData) =>
          prevData.filter((ride) => ride.id !== rideToDelete.id)
        );
      } catch (error) {
        console.error("Error deleting document: ", error);
      }
      closeDeleteModal();
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
  };

  const openDeactivateModal = (user) => {
    setUserToDeactivate(user);
    setIsDeactivateModalOpen(true);
  };

  const closeDeactivateModal = () => {
    setUserToDeactivate(null);
    setIsDeactivateModalOpen(false);
  };

  const handleDeactivateSuccess = () => {
    fetchData(); // Refresh the data after deactivation/reactivation
  };

  const exportToPDF = () => {
    if (!data || data.length === 0) {
      alert("No data to export");
      return;
    }

    const doc = new jsPDF();

    // Add title
    doc.setFontSize(24);
    doc.setTextColor(59, 130, 246); // Blue color
    doc.text("Passenger List", 105, 25, { align: "center" });

    // Add subtitle with date
    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128); // Gray color
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 35, {
      align: "center",
    });

    // Add line separator
    doc.setDrawColor(229, 231, 235);
    doc.line(20, 45, 190, 45);

    // Prepare table data
    const tableData = data.map((passenger, index) => [
      index + 1,
      passenger.fullName || "N/A",
      passenger.email || "N/A",
      passenger.phoneNumber || "N/A",
    ]);

    // Add table using autoTable plugin
    autoTable(doc, {
      startY: 55,
      head: [["#", "Name", "Email Address", "Phone Number"]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 10,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 8,
        textColor: 55,
        lineColor: [229, 231, 235],
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      margin: { top: 20, right: 20, bottom: 20, left: 20 },
      styles: {
        cellPadding: 2,
        lineWidth: 0.5,
      },
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Page ${i} of ${pageCount}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    // Save the PDF
    const now = new Date();

    const dateStr = now
      .toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
      .replace(/\//g, "-"); // Replace slashes with dashes

    const fileName = `Passenger List ${dateStr}.pdf`;
    doc.save(fileName);
  };

  const columns = useMemo(
    () => [
      { Header: "Name", accessor: "fullName" },
      { Header: "Email Address", accessor: "email" },
      { Header: "Phone", accessor: "phoneNumber" },
      { Header: "Role", accessor: "role" },
      {
        Header: "Account Status",
        accessor: "accountStatus",
        Cell: ({ value }) => (
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${value === "deactivated"
                ? "bg-red-100 text-red-800"
                : "bg-green-100 text-green-800"
              }`}
          >
            {value === "deactivated" ? "Deactivated" : "Active"}
          </span>
        ),
      },
      {
        Header: (
          <div
            onClick={toggleSortOrder}
            className="cursor-pointer flex gap-1 items-center"
          >
            Created At{" "}
            {sortOrder === "desc" ? <IoIosArrowDown /> : <IoIosArrowUp />}
          </div>
        ),
        accessor: "createdAt",
        Cell: ({ value }) => (value ? value.toLocaleDateString() : "-----"),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex gap-2 items-center">
            <button
              className="text-primary hover:text-dazzledBlue text-lg"
              onClick={() => openModal(row.original)}
              title="View Details"
            >
              <FiEye />
            </button>
            <button
              className={`text-lg ${row.original.accountStatus === "deactivated"
                  ? "text-green-600 hover:text-green-700"
                  : "text-red-600 hover:text-red-700"
                }`}
              onClick={() => openDeactivateModal(row.original)}
              title={
                row.original.accountStatus === "deactivated"
                  ? "Reactivate Account"
                  : "Deactivate Account"
              }
            >
              {row.original.accountStatus === "deactivated" ? (
                <FaUserCheck />
              ) : (
                <FaUserSlash />
              )}
            </button>
          </div>
        ),
      },
    ],
    [sortOrder]
  );

  useEffect(() => {
    fetchData();
  }, [sortOrder, statusFilter]);

  return (
    <div className="px-1 md-px-4 min-h-screen">
      <div className="flex items-center justify-between mt-8 md:mt-5 mb-2 md:mb-5">
        <h1 className="text-2xl md:text-3xl font-bold text-primary">
          Passenger List
        </h1>
        <button
          onClick={exportToPDF}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
        >
          <FiDownload className="text-lg" />
          Export PDF
        </button>
      </div>

      {/* Status Filter */}
      <div className="flex mb-4 items-center gap-2">
        <span className="text-gray-700 font-medium">Filter by Status:</span>
        <div className="flex space-x-1">
          <button
            className={`px-4 py-2 rounded-md transition-colors ${statusFilter === "all"
                ? "bg-celadon text-primary"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setStatusFilter("all")}
          >
            All
          </button>
          <button
            className={`px-4 py-2 rounded-md transition-colors ${statusFilter === "active"
                ? "bg-celadon text-primary"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setStatusFilter("active")}
          >
            Active
          </button>
          <button
            className={`px-4 py-2 rounded-md transition-colors ${statusFilter === "deactivated"
                ? "bg-celadon text-primary"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setStatusFilter("deactivated")}
          >
            Deactivated
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        data && (
          <div className="">
            <div className="">
              <Table columns={columns} data={data} />
            </div>

            <UserDetailModal
              isOpen={isModalOpen}
              onRequestClose={closeModal}
              user={selectedUser}
            />
            <DeleteConfirmationModal
              isOpen={isDeleteModalOpen}
              onRequestClose={closeDeleteModal}
              onConfirm={handleDelete}
            />
            <DeactivateAccountModal
              isOpen={isDeactivateModalOpen}
              onRequestClose={closeDeactivateModal}
              user={userToDeactivate}
              onSuccess={handleDeactivateSuccess}
            />
          </div>
        )
      )}
    </div>
  );
};

export default Home;

